import React, {useState} from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import {Button, FormField, Input, Loader, Text} from 'wix-style-react';

import {useTranslate} from '@wix/premium-bricks-translate';

import {DomainNameValidator} from '../../utils/DomainNameValidators';
import {WhoisLookupParams} from '../../WhoisWeb';
import {Captcha} from '../Captcha';

import s from './WhoisForm.scss';

export interface WhoisFormProps {
  reCaptchaSiteKey: string;
  isAuthorized: boolean;
  dataHook?: string;
  isLoading?: boolean;
  errors?: null | {[key: string]: string | null};
  onSubmit(data: WhoisLookupParams): Promise<void>;
}

export interface WhoisFormViewState {
  domainName: string | undefined;
  token: null | string;
  errors: null | {[key: string]: string};
}

export const WhoisForm = (props: WhoisFormProps) => {
  const {t} = useTranslate();
  const [state, setState] = useState<WhoisFormViewState>({
    domainName: '',
    token: null,
    errors: null,
  });

  const {isAuthorized, dataHook, reCaptchaSiteKey, isLoading} = props;

  let captchaInstance: ReCAPTCHA;

  const handleGetCaptchaApi = (captcha: ReCAPTCHA) => {
    captchaInstance = captcha;
  };

  const handleCaptchaChange = () => {
    setState({
      ...state,
      errors: {
        ...state.errors,
        'g-recaptcha-response': '',
      },
    });
  };

  const handleDomainNameChange = () => {
    setState({
      ...state,
      errors: {
        ...state.errors,
        domainName: '',
      },
    });
  };

  const validateFormData = (data: {domainName?: string; 'g-recaptcha-response'?: string}) => {
    let domainError: string;

    if (!data.domainName) {
      domainError = 'whois.form.validation.error.empty_domain';
    } else {
      const domainValidator = new DomainNameValidator(data.domainName);
      const isDomainValid = domainValidator.validate();
      domainError = isDomainValid ? '' : 'whois.form.validation.error.wrong_domain';
    }

    const isValid = !domainError && (isAuthorized || data['g-recaptcha-response']);
    return {
      isValid,
      errors: {
        domainName: domainError,
        'g-recaptcha-response':
          isAuthorized || data['g-recaptcha-response'] ? '' : 'whois.form.validation.error.captcha_required',
      },
    };
  };

  const formatRequestParams = (htmlFormElements: NodeListOf<HTMLInputElement | HTMLTextAreaElement>) => {
    const htmlFormElementsArray: HTMLInputElement[] = Array.prototype.slice.call(htmlFormElements);

    return htmlFormElementsArray.reduce((memo, formElement) => {
      memo[formElement.name] = formElement.value ? formElement.value.trim() : '';

      return memo;
    }, {} as any);
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event && event.preventDefault();

    const formElements = (event.target as HTMLFormElement).querySelectorAll<HTMLInputElement | HTMLTextAreaElement>(
      'input, textarea',
    );

    const requestParams: {
      domainName: string;
      'g-recaptcha-response': string; // default name of the google reCaptcha element
    } = formatRequestParams(formElements);

    const validationResult = validateFormData(requestParams);

    if (!validationResult.isValid) {
      setState({
        ...state,
        errors: validationResult.errors,
      });
      return;
    }

    await props.onSubmit({
      domainName: requestParams.domainName,
      token: requestParams['g-recaptcha-response'],
    });

    captchaInstance && captchaInstance.reset();
  };

  const {errors} = state;

  return (
    <form data-hook={dataHook} onSubmit={handleSubmit}>
      <div className={`${s.domainNameFieldWrapper} ${errors && errors.domainName ? s.error : ''}`}>
        <FormField>
          <Input
            type="text"
            id="domainName"
            name="domainName"
            defaultValue=""
            dataHook="whois-domain-name-input"
            placeholder={t('whois.form.domain_name.placeholder')}
            onChange={handleDomainNameChange}
            maxLength={253}
            status={errors && errors.domainName ? 'error' : undefined}
            size="large"
          />
          {errors && errors.domainName && (
            <div className={s.inputErrorWrapper}>
              <Text weight="normal" size="small" skin="error" dataHook="whois-domain-name-input-error">
                {t(errors.domainName)}
              </Text>
            </div>
          )}
        </FormField>
      </div>

      {!isAuthorized && (
        <div className={`${s.captchaFieldWrapper} ${errors && errors['g-recaptcha-response'] ? s.error : ''}`}>
          <Captcha
            dataHook="whois-captcha"
            onChange={handleCaptchaChange}
            siteKey={reCaptchaSiteKey}
            onGetApi={handleGetCaptchaApi}
          />

          {errors && errors['g-recaptcha-response'] && (
            <div className={s.inputErrorWrapper}>
              <Text weight="normal" size="small" skin="error" dataHook="whois-captcha-error">
                {t(errors['g-recaptcha-response'])}
              </Text>
            </div>
          )}
        </div>
      )}

      <Button upgrade="true" type="submit" dataHook="whois-submit-button">
        <span className={`${s.submitButtonText} ${isLoading ? s.loading : ''}`}>
          {isLoading ? <Loader size="tiny" color="white" /> : t('whois.form.submit_button')}
        </span>
      </Button>
    </form>
  );
};
